import { Box, Paper, styled } from "@mui/material";
import React, { JSX, ReactNode } from "react";
import { DynamicTextSection } from "./dynamiccontent/DynamicTextSection";
import { DynamicTextParagraph } from "./dynamiccontent/DynamicTextParagraph";
import { DynamicTextHeader } from "./dynamiccontent/DynamicTextHeader";
import { DynamicTextSubtitle } from "./dynamiccontent/DynamicTextSubtitle";
import { DynamicHtmlList } from "./dynamiccontent/DynamicHtmlList";
import { ImageComponentProps } from "./ImageComponent";
import { DynamicImage } from "./dynamiccontent/DynamicImage";
import { DynamicStack } from "./dynamiccontent/DynamicStack";

interface DynamicContentCommonBase {
  key?: string;
  childElements?: React.ReactNode[];
}

interface DynamicContentItemBase {
  itemType: string;
}

export interface DynamicContentText extends DynamicContentCommonBase {
  text: string;
  sxProps?: {};
}

export interface DynamicContentHtmlListItem {
  primaryText: string;
  secondaryText?: string;
  primaryLink?: string;
}

export interface DynamicContentHtmlList extends DynamicContentCommonBase {
  listType: string;
  listItems: string[] | {}[];
  sxProps?: {};
}

export interface DynamicStackContent extends DynamicContentCommonBase {
  direction: string;
  stackItems: ReactNode[];
  sxProps?: {};
  containerType?: string; // Box supported as alternative to Paper
}


export type DynamicContentImage = DynamicContentCommonBase & ImageComponentProps

// More not currently used but here to show how to add more types
export interface DynamicContentMore extends DynamicContentCommonBase {
  moreContent: string;
}  

// More not currently used but here to show how to add more types
export type DynamicContentAllTypes = DynamicContentText | DynamicContentHtmlList | DynamicStackContent |DynamicContentMore 

export type DynamicContentItem = DynamicContentAllTypes & DynamicContentItemBase 

export interface DynamicContentContainerProps {
  items: DynamicContentItem[];
  key: string;
  containerType?: string; // Box supported as alternative to Paper
}

enum DynamicContentTextTypes { 
  Section = "section", 
  Header = "header", 
  Paragrapg = "paragraph",
  Subtitle = "subtitle"
}

const isDynamicTextType = (itemtype: string): itemtype is DynamicContentTextTypes => {
  return itemtype in DynamicContentTextTypes;
} 

const isDynamicContentItem = (item: any): item is DynamicContentItem => {
  return typeof item === "object" && item !== null && "itemType" in item;   
}

const isDynamicContentText = (item: any): item is DynamicContentText => {
  return typeof item === "object" && item !== null && "text" in item;   
}

const DynamicContentPaper = styled(Paper)(({ theme }) => ({
  width: "100%",
  //height: 120,
  padding: theme.spacing(2),
  ...theme.typography.body2,
  textAlign: 'left',
}));

const DynamicContentBox = styled(Box)(({ theme }) => ({
  width: "100%",
  //height: 120,
  padding: theme.spacing(2),
  ...theme.typography.body2,
  textAlign: 'left',
}));

export const DynamicContentComponent = ({ items, containerType, key }: DynamicContentContainerProps) => {
  

  if (!items || items.length == 0) {
    return <div></div>;
  }

  const createComponent = (item: DynamicContentItem, key: string): JSX.Element => {
    // Call recursively for children
    let children = [] as ReactNode[];
    if (item.childElements && item.childElements.length > 0) {
      children = item.childElements.map((dce, dceIndex) => {
        if (isDynamicContentItem(dce)) {
          return createComponent(dce, `${key}-dynamic-child-${dceIndex}`);
        } else {
          return dce;
        }
      })
      console.log("children" + children);
    }
    switch(item.itemType) {
      case "section": {
        // Call recursively for children
        return React.createElement(DynamicTextSection, {
          text: "text" in item ? item.text : '',
          childElements: children,
          key: `${key}`,
          sxProps: "sxProps" in item ? item.sxProps : undefined
        });
      }
      case "paragraph": {
        return React.createElement(DynamicTextParagraph, {
          text: "text" in item ? item.text : '',
          childElements: children,
          key: `${key}`
        });
      }
      case "header": {
        return React.createElement(DynamicTextHeader, {
          text: "text" in item ? item.text : '',
          childElements: children,
          key: `${key}`
        });
      }
      case "subtitle": {
        return React.createElement(DynamicTextSubtitle, {
          text: "text" in item ? item.text : '',
          childElements: children,
          key: `${key}`
        });
      }
      case "htmllist": {
        return React.createElement(DynamicHtmlList, {
          listType: "listType" in item ? item.listType : '',
          listItems: "listItems" in item? item.listItems: [],
          sxProps: "sxProps" in item ? item.sxProps : undefined,
          childElements: children,
          key: `${key}`
        });
      }
      case "image": {
        return React.createElement(DynamicImage, {
          imageSrc: "imageSrc" in item ? item.imageSrc as string: '',
          imageProps: "imageProps" in item? item.imageProps as object: {},
          variant: "variant" in item? item.variant as "whitebackground" | undefined : undefined,
          childElements: children,
          key: `${key}`
        });
      }
      case "stack": {
        // stack elements has to processed as dynamic items
        let stackElements = [] as ReactNode[];
        if ("stackItems" in item && item.stackItems.length > 0) {
          stackElements = item.stackItems.map((dsi, dsIndex) => {
            if (isDynamicContentItem(dsi)) {
              return createComponent(dsi, `${key}-dynamic-child-${dsIndex}`);
            } else {
              return dsi;
            }
          })
        }  

        return React.createElement(DynamicStack, {
          direction: "direction" in item ? item.direction : '',
          stackItems: stackElements,
          containerType: "containerType" in item ? item.containerType : undefined,
          sxProps: "sxProps" in item ? item.sxProps : undefined,
          childElements: children,
          key: `${key}`
        });
      }
      default: {
        return <div>No component match found!</div>;
      }
    }
  };

  const dynamicsComponents = [] as JSX.Element[];
  // 1. Loop through content and for each
  //   a) Determine type
  //   b) Add compont with content as specified

  items.forEach((item, index ) => {
    const component = createComponent(item, `${key}-component-${index}`);
    dynamicsComponents[index] = component; 
  });

  if (!dynamicsComponents || dynamicsComponents.length == 0) {
    return <div></div>;
  }

  switch(containerType) {
    case "Box": {
      return (
        <Box>
          <DynamicContentBox>
            {dynamicsComponents}
         </DynamicContentBox>
        </Box>
      );  
    }
    default: {
      return (
        <Box>
          <DynamicContentPaper>
            {dynamicsComponents}
         </DynamicContentPaper>
        </Box>
      );
    }
  }

}